import React from 'react';
import { Loader2, Activity, Dumbbell, Heart, Apple } from 'lucide-react';
import { cn } from '../lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'fitness' | 'health' | 'nutrition';
  text?: string;
  className?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

const variantIcons = {
  default: Loader2,
  fitness: Dumbbell,
  health: Heart,
  nutrition: Apple,
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'default',
  text,
  className,
}) => {
  const Icon = variantIcons[variant];
  const isAnimated = variant === 'default';

  return (
    <div className={cn('flex flex-col items-center justify-center space-y-2', className)}>
      <div className="relative">
        <Icon
          className={cn(
            sizeClasses[size],
            'text-primary-600',
            isAnimated && 'animate-spin'
          )}
        />
        {isAnimated && (
          <div className="absolute inset-0 rounded-full border-2 border-primary-200 animate-pulse" />
        )}
      </div>
      {text && (
        <p className="text-sm text-gray-600 animate-pulse">{text}</p>
      )}
    </div>
  );
};

// Skeleton loading components
export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('animate-pulse', className)}>
    <div className="bg-gray-200 rounded-lg h-48 w-full"></div>
    <div className="mt-4 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
    </div>
  </div>
);

export const SkeletonText: React.FC<{ 
  lines?: number; 
  className?: string;
  width?: string;
}> = ({ lines = 1, className, width = 'w-full' }) => (
  <div className={cn('space-y-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className={cn(
          'h-4 bg-gray-200 rounded animate-pulse',
          i === lines - 1 ? 'w-3/4' : width
        )}
      />
    ))}
  </div>
);

export const SkeletonMetric: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('animate-pulse p-4', className)}>
    <div className="flex items-center space-x-3">
      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
      <div className="space-y-2 flex-1">
        <div className="h-6 bg-gray-200 rounded w-16"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
    </div>
  </div>
);

// Full page loading component
export const FullPageLoading: React.FC<{ 
  text?: string;
  variant?: 'default' | 'fitness' | 'health' | 'nutrition';
}> = ({ text = 'Loading...', variant = 'fitness' }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <LoadingSpinner size="xl" variant={variant} text={text} />
    </div>
  </div>
);